// src/components/work/WorkList.jsx
import React, {
  useMemo,
  useState,
  useCallback,
  useRef,
  useEffect,
  useLayoutEffect,
} from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import {
  FiEdit2,
  FiTrash2,
  FiChevronLeft,
  FiChevronRight,
  FiMoreVertical,
  FiChevronUp,
  FiChevronDown,
} from 'react-icons/fi';

/**
 * WorkList — Production-grade SaaS table for Tasks • Jobs • Appointments
 * Pure presentational component (no fetching). Feed data via hooks/services.
 *
 * Contract notes:
 * - onOpenDetails: expects a string workId (not the whole item)
 */

function cx(...xs) {
  return xs.filter(Boolean).join(' ');
}

function initials(name = '') {
  const parts = String(name).trim().split(/\s+/).filter(Boolean).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() ?? '').join('');
}

// Match Invoices date format (date only, locale-based)
const fmtDate = (value) => (value ? new Date(value).toLocaleDateString() : '—');

function titleForType(type) {
  if (type === 'job') return 'Job';
  if (type === 'appointment') return 'Appointment';
  return 'Task';
}

// Calendar-aligned type dot colors
function dotClassForType(t) {
  const k = String(t || '').toLowerCase();
  if (k === 'job') return 'bg-amber-500';
  if (k === 'appointment') return 'bg-violet-500';
  if (k === 'conflict') return 'bg-rose-500';
  return 'bg-sky-500'; // task/default
}

// ----- transition hints (keep in sync with backend) -----
const HINT_TRANSITIONS = {
  task: {
    todo: ['in_progress', 'blocked', 'done', 'cancelled'],
    in_progress: ['blocked', 'done', 'cancelled'],
    blocked: ['in_progress', 'done', 'cancelled'],
    done: ['in_progress', 'cancelled'],
    cancelled: ['todo'],
  },
  job: {
    new: ['scheduled', 'en_route', 'in_progress', 'cancelled'],
    scheduled: ['en_route', 'in_progress', 'cancelled'],
    en_route: ['in_progress', 'cancelled'],
    in_progress: ['completed', 'cancelled', 'no_show'],
    completed: ['invoiced', 'cancelled'],
    invoiced: [],
    cancelled: [],
    no_show: ['scheduled'],
  },
  appointment: {
    planned: ['confirmed', 'in_progress', 'cancelled', 'no_show'],
    confirmed: ['in_progress', 'cancelled', 'no_show'],
    in_progress: ['done', 'cancelled'],
    done: [],
    cancelled: [],
    no_show: ['planned'],
  },
};

function nextStatusesFor(type, status, item) {
  const base = HINT_TRANSITIONS[type]?.[status] ?? [];
  if (type === 'job') {
    return base.map((s) => ({
      value: s,
      disabled: s === 'invoiced' && !item?.invoiceId,
      reason: s === 'invoiced' && !item?.invoiceId ? 'Link an invoice first' : undefined,
    }));
  }
  return base.map((s) => ({ value: s, disabled: false }));
}

// Chip classes to mirror invoice pill style
const WORK_STATUS_CHIP = {
  todo: 'bg-slate-100 text-slate-700 border border-slate-200',
  new: 'bg-slate-100 text-slate-700 border border-slate-200',
  planned: 'bg-slate-100 text-slate-700 border border-slate-200',

  scheduled: 'bg-indigo-100 text-indigo-800 border border-indigo-200',
  confirmed: 'bg-indigo-100 text-indigo-800 border border-indigo-200',
  en_route: 'bg-sky-100 text-sky-700 border border-sky-200',
  in_progress: 'bg-blue-100 text-blue-700 border border-blue-200',
  blocked: 'bg-rose-100 text-rose-700 border border-rose-200',

  done: 'bg-green-100 text-green-700 border border-green-200',
  completed: 'bg-green-100 text-green-700 border border-green-200',
  invoiced: 'bg-green-100 text-green-700 border border-green-200',

  cancelled: 'bg-zinc-100 text-zinc-800 border border-zinc-200',
  no_show: 'bg-orange-100 text-orange-800 border border-orange-200',
};

function prettyStatus(s) {
  if (!s) return '—';
  if (s === 'written_off') return 'Written Off';
  return String(s)
    .split('_')
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ''))
    .join(' ');
}

/* ----------------------------- subcomponents ----------------------------- */

// UPDATED: Accept ids or snapshot objects; normalize to {id,name,avatarUrl}
function AssigneeGroup({ assignees = [] }) {
  const norm = (assignees || []).map((a, idx) => {
    if (a && typeof a === 'object') {
      const id = a.id || a._id || String(idx);
      const name = a.name || a.email || `User ${String(id).slice(-6)}`;
      return { id: String(id), name, avatarUrl: a.avatarUrl || null };
    }
    const id = String(a);
    return { id, name: `User ${id.slice(-6)}`, avatarUrl: null };
  });
  if (!norm.length) return <span className="text-slate-400">—</span>;
  const shown = norm.slice(0, 3);
  const extra = norm.length - shown.length;
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {shown.map((u) => (
        <span
          key={u.id}
          className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-700"
          title={u.name}
        >
          {u.name}
        </span>
      ))}
      {extra > 0 && (
        <span className="text-xs text-slate-500 dark:text-slate-400">+{extra} more</span>
      )}
    </div>
  );
}

function HeaderCell({
  children,
  className,
  sortable = false,
  sortActive = false,
  sortDir = 'asc',
  onClick,
}) {
  return (
    <th
      scope="col"
      className={cx(
        'px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300',
        sortable ? 'cursor-pointer select-none' : '',
        className
      )}
      onClick={sortable ? onClick : undefined}
      aria-sort={sortActive ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <div className="inline-flex items-center gap-1">
        <span>{children}</span>
        {sortable && (
          <span className="text-slate-400">
            {sortActive ? (sortDir === 'asc' ? <FiChevronUp /> : <FiChevronDown />) : (
              <FiChevronUp className="opacity-40" />
            )}
          </span>
        )}
      </div>
    </th>
  );
}

function LoadingRows({ rows = 8, withCheckbox = false }) {
  return (
    <>
      {Array.from({ length: rows }).map((_, i) => (
        <tr key={i} className="animate-pulse">
          {withCheckbox && (
            <td className="px-3 py-3">
              <div className="h-4 w-4 rounded bg-slate-200 dark:bg-slate-800" />
            </td>
          )}
          {/* Work # skeleton (new) */}
          <td className="px-4 py-3">
            <div className="h-4 w-24 rounded bg-slate-200 dark:bg-slate-800" />
          </td>
          <td className="px-4 py-3">
            <div className="flex items-center gap-2">
              <div className="h-3 w-3 rounded-full bg-slate-200 dark:bg-slate-800" />
              <div className="h-4 w-40 rounded bg-slate-200 dark:bg-slate-800" />
            </div>
          </td>
          <td className="px-4 py-3">
            <div className="h-4 w-28 rounded bg-slate-200 dark:bg-slate-800" />
          </td>
          <td className="px-4 py-3">
            <div className="h-5 w-20 rounded-full bg-slate-200 dark:bg-slate-800" />
          </td>
          <td className="px-4 py-3">
            <div className="flex -space-x-2">
              <div className="h-7 w-7 rounded-full bg-slate-200 dark:bg-slate-800 ring-2 ring-white dark:ring-slate-900" />
              <div className="h-7 w-7 rounded-full bg-slate-200 dark:bg-slate-800 ring-2 ring-white dark:ring-slate-900" />
            </div>
          </td>
          <td className="px-4 py-3">
            <div className="h-4 w-28 rounded bg-slate-200 dark:bg-slate-800" />
          </td>
          <td className="px-2 py-2">
            <div className="h-6 w-6 rounded-full bg-slate-200 dark:bg-slate-800" />
          </td>
        </tr>
      ))}
    </>
  );
}

/* --------- Portaled Row Actions (prevents clipping inside scroll shells) --------- */
function RowActions({
  item,
  type,
  canEdit,
  canTransition,
  canDelete,
  onEdit,
  onTransition,
  onDelete,
  /** NEW: prefer opening the details drawer */
  onOpenDetails,
}) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const rowType = String(item?.type || type || '').toLowerCase();
  const options = useMemo(() => nextStatusesFor(rowType, item?.status, item), [rowType, item]);
  const [style, setStyle] = useState({ top: 0, left: 0, origin: 'top-right' });

  const close = useCallback(() => setOpen(false), []);

  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    const vh = window.innerHeight;
    const vw = window.innerWidth;

    const menuW = 224; // ~w-56
    const gap = 8;

    let left = r.right - menuW;
    if (left < 8) left = Math.max(8, r.left);
    let top = r.bottom + gap;
    let origin = 'top-right';

    setStyle({ top, left, origin });

    requestAnimationFrame(() => {
      const el = menuRef.current;
      if (!el) return;
      const h = el.offsetHeight;
      const w = el.offsetWidth;
      if (top + h > vh - 8) {
        top = Math.max(8, r.top - h - gap);
        origin = 'bottom-right';
      }
      if (left + w > vw - 8) {
        left = Math.max(8, vw - w - 8);
      }
      setStyle({ top, left, origin });
    });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (!menuRef.current || !btnRef.current) return;
      if (menuRef.current.contains(e.target)) return;
      if (btnRef.current.contains(e.target)) return;
      close();
    };
    const onKey = (e) => e.key === 'Escape' && close();
    const onScrollOrResize = () => close();

    document.addEventListener('mousedown', onDoc, true);
    window.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      document.removeEventListener('mousedown', onDoc, true);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open, close]);

  return (
    <div className="relative">
      <button
        ref={btnRef}
        className="p-2 rounded-md hover:bg-slate-100 text-slate-600 dark:hover:bg-slate-800 dark:text-slate-300"
        aria-label="Row actions"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <FiMoreVertical />
      </button>

      {open &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            style={{ position: 'fixed', top: style.top, left: style.left, zIndex: 60 }}
            className={`min-w-[224px] rounded-xl border border-slate-200 bg-white shadow-xl origin-${style.origin} dark:border-slate-800 dark:bg-slate-900`}
          >
            <div className="py-1 text-sm">
              <button
                role="menuitem"
                disabled={!canEdit}
                onClick={() => {
                  close();
                  const wid = String(item?._id || item?.id || '');
                  if (onOpenDetails && wid) {
                    onOpenDetails(wid); // ✅ open details drawer instead of EditWorkModal
                  } else if (onEdit) {
                    onEdit(item); // fallback
                  }
                }}
                className={cx(
                  'flex w-full items-center gap-2 px-3 py-2 text-left',
                  canEdit
                    ? 'hover:bg-slate-50 text-slate-700 dark:text-slate-200 dark:hover:bg-slate-800'
                    : 'opacity-50 cursor-not-allowed text-slate-400'
                )}
              >
                <FiEdit2 className="text-slate-500" />
                Edit
              </button>

              <div className="px-3 pt-2 pb-1 text-[11px] uppercase tracking-wide text-slate-400">
                Move to
              </div>

              {options.length === 0 && (
                <div className="px-3 py-2 text-xs text-slate-400">No actions</div>
              )}

              {options.map((opt) => (
                <button
                  key={opt.value}
                  role="menuitem"
                  disabled={!canTransition || opt.disabled}
                  title={opt.disabled ? opt.reason : undefined}
                  onClick={() => {
                    close();
                    if (onTransition) onTransition(item, opt.value);
                  }}
                  className={cx(
                    'flex w-full items-center gap-2 px-3 py-2 text-left',
                    !opt.disabled && canTransition
                      ? 'hover:bg-slate-50 text-slate-700 dark:text-slate-200 dark:hover:bg-slate-800'
                      : 'opacity-50 cursor-not-allowed text-slate-400'
                  )}
                >
                  <span className="inline-block h-2 w-2 rounded-full bg-slate-300" />
                  <span className="capitalize">{opt.value.replace(/_/g, ' ')}</span>
                </button>
              ))}

              <div className="my-1 border-t border-slate-200 dark:border-slate-800" />

              <button
                role="menuitem"
                disabled={!canDelete}
                onClick={() => {
                  close();
                  if (onDelete) onDelete(item);
                }}
                className={cx(
                  'flex w-full items-center gap-2 px-3 py-2 text-left',
                  canDelete
                    ? 'hover:bg-rose-50 text-rose-600 dark:hover:bg-rose-950/30'
                    : 'opacity-50 cursor-not-allowed text-slate-400'
                )}
              >
                <FiTrash2 />
                Delete
              </button>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}

/* ----------------------------- main ----------------------------- */
export default function WorkList({
  type,
  items = [],
  meta,
  isLoading = false,
  isFetching = false,
  error,
  canEdit = true,
  canTransition = true,
  canDelete = true,
  onEdit,
  onTransition,
  onDelete,
  onPageChange,
  sort,
  onSortChange,
  selectable = false,
  selectedIds = [],
  onToggleSelect,
  onToggleSelectAll,

  /** onOpenDetails expects a string workId */
  onOpenDetails,
}) {
  const page = Math.max(1, Number(meta?.page || 1));
  const totalPages = Math.max(1, Number(meta?.totalPages || 1));
  const total = typeof meta?.total === 'number' ? meta.total : undefined;

  const emptyLabel = useMemo(() => {
    if (type === 'job') return 'No jobs found';
    if (type === 'appointment') return 'No appointments in this range';
    return 'No tasks yet';
  }, [type]);

  const idsOnPage = useMemo(
    () => items.map((it) => String(it._id || it.id || '')),
    [items]
  );
  const allChecked =
    selectable && idsOnPage.length > 0 && idsOnPage.every((id) => selectedIds.includes(id));
  const someChecked =
    selectable && !allChecked && idsOnPage.some((id) => selectedIds.includes(id));

  const toggleAll = useCallback(
    (e) => {
      if (onToggleSelectAll) onToggleSelectAll(e.target.checked, idsOnPage);
    },
    [onToggleSelectAll, idsOnPage]
  );

  const sortToggle = useCallback(
    (field) => {
      if (!onSortChange) return;
      const dir =
        sort?.field === field ? (sort?.dir === 'asc' ? 'desc' : 'asc') : 'asc';
      onSortChange({ field, dir });
    },
    [sort, onSortChange]
  );

  const dateHeaderLabel = useMemo(() => {
    if (type === 'appointment') return 'Start';
    if (type === 'job') return 'Scheduled/Due';
    if (type === 'task') return 'Due';
    return 'Date';
  }, [type]);

  const dateSortField = useMemo(
    () => (type === 'appointment' ? 'schedule.start' : 'schedule.end'),
    [type]
  );

  // Safe getter for display number
  const getDisplayNumber = useCallback((it) => {
    return it?.displayWorkNumber || it?.workNumber || it?.code || null;
  }, []);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-950">
      {isFetching && !isLoading && (
        <div className="h-0.5 w-full animate-pulse rounded-t-2xl bg-gradient-to-r from-transparent via-indigo-400 to-transparent" />
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800 text-sm">
          <thead className="sticky top-0 z-10 bg-slate-50/90 backdrop-blur dark:bg-slate-900/80">
            <tr>
              {selectable && (
                <th className="w-10 px-3 py-2">
                  <label className="inline-flex items-center gap-2">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 dark:border-slate-700 dark:bg-slate-900"
                      checked={allChecked}
                      ref={(el) => {
                        if (el) el.indeterminate = someChecked;
                      }}
                      onChange={toggleAll}
                      aria-label="Select all rows"
                    />
                  </label>
                </th>
              )}
              {/* Work # column */}
              <HeaderCell
                sortable
                sortActive={sort?.field === 'workNumber'}
                sortDir={sort?.dir}
                onClick={() => sortToggle('workNumber')}
                className="whitespace-nowrap"
              >
                Work #
              </HeaderCell>
              <HeaderCell
                sortable
                sortActive={sort?.field === 'title'}
                sortDir={sort?.dir}
                onClick={() => sortToggle('title')}
              >
                Title
              </HeaderCell>
              <HeaderCell
                sortable
                sortActive={sort?.field === 'client'}
                sortDir={sort?.dir}
                onClick={() => sortToggle('client')}
              >
                Client
              </HeaderCell>
              <HeaderCell>Status</HeaderCell>
              {/* ✅ NEW: Assignees sortable via 'assignee' key (maps to sortKeys.primaryAssigneeName) */}
              <HeaderCell
                sortable
                sortActive={sort?.field === 'assignee'}
                sortDir={sort?.dir}
                onClick={() => sortToggle('assignee')}
              >
                Assignees
              </HeaderCell>
              <HeaderCell
                sortable
                sortActive={sort?.field === dateSortField}
                sortDir={sort?.dir}
                onClick={() => sortToggle(dateSortField)}
              >
                {dateHeaderLabel}
              </HeaderCell>
              <HeaderCell className="w-12"> </HeaderCell>
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-100 dark:divide-slate-900">
            {isLoading ? (
              <LoadingRows rows={8} withCheckbox={selectable} />
            ) : error ? (
              <tr>
                <td
                  colSpan={selectable ? 8 : 7}
                  className="px-4 py-8 text-center text-sm text-rose-600 dark:text-rose-300"
                >
                  {String(error?.message || 'Failed to load work items')}
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={selectable ? 8 : 7} className="px-4 py-10 text-center">
                  <div className="text-sm text-slate-500 dark:text-slate-400">
                    {emptyLabel}
                  </div>
                </td>
              </tr>
            ) : (
              items.map((item) => {
                const id = String(item._id || item.id || '');
                const rowType = String(item.type || type || 'task').toLowerCase();
                const title = item.title || '(Untitled)';

                // Prefer DTO clientSnapshot
                const clientId =
                  item?.clientSnapshot?.id ||
                  item?.client?._id ||
                  item?.clientId ||
                  '';
                const clientName =
                  item?.clientSnapshot?.name ||
                  item?.client?.name ||
                  item?.clientName ||
                  '';

                // Prefer DTO assigneeSnapshots
                const assignees =
                  (Array.isArray(item?.assigneeSnapshots) && item.assigneeSnapshots.length
                    ? item.assigneeSnapshots
                    : Array.isArray(item?.assignees)
                    ? item.assignees
                    : []);

                const isAppt = rowType === 'appointment';
                const start = item.schedule?.start;
                const end = item.schedule?.end;
                const dateText = fmtDate(isAppt ? start : (end || start));

                const checked = selectable && selectedIds.includes(id);
                const number = getDisplayNumber(item);

                return (
                  <tr
                    key={id}
                    className="hover:bg-blue-50/40 dark:hover:bg-slate-900/50"
                    data-testid={`work-row-${id}`}
                  >
                    {selectable && (
                      <td className="px-3 py-3">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 dark:border-slate-700 dark:bg-slate-900"
                          checked={checked}
                          onChange={() => {
                            if (onToggleSelect) onToggleSelect(id);
                          }}
                          aria-label={`Select ${titleForType(rowType)}: ${title}`}
                        />
                      </td>
                    )}

                    {/* Work # (click to open details drawer) */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      {number ? (
                        <button
                          type="button"
                          className="font-semibold text-blue-700 hover:underline"
                          onClick={() => {
                            if (onOpenDetails) {
                              const wid = String(item._id || item.id || '');
                              if (wid) onOpenDetails(wid); // ✅ pass id, not whole item
                            } else if (onEdit) {
                              onEdit(item); // graceful fallback
                            }
                          }}
                          title={`Open ${titleForType(rowType)} details`}
                        >
                          {number}
                        </button>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                      <div className="text-xs text-slate-500 capitalize">
                        {rowType}
                      </div>
                    </td>

                    {/* Title + Type dot */}
                    <td className="px-4 py-3">
                      <div className="flex min-w-0 items-center gap-2">
                        <div
                          className={cx(
                            'h-2.5 w-2.5 rounded-full',
                            dotClassForType(rowType)
                          )}
                          title={rowType}
                        />
                        <div className="min-w-0">
                          <div className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">
                            <button
                              type="button"
                              className="hover:underline"
                              onClick={() => {
                                const wid = String(item._id || item.id || '');
                                if (onOpenDetails && wid) {
                                  onOpenDetails(wid); // ✅ open drawer from title
                                } else if (onEdit) {
                                  onEdit(item); // fallback
                                }
                              }}
                              title={`Open ${titleForType(rowType)} details`}
                            >
                              {title}
                            </button>
                          </div>
                          {!!item.description && (
                            <div className="max-w-[36rem] truncate text-xs text-slate-500 dark:text-slate-400">
                              {item.description}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* Client */}
                    <td className="px-4 py-3">
                      {clientId && clientName ? (
                        <Link
                          to={`/clients/${encodeURIComponent(String(clientId))}`}
                          className="text-slate-900 font-medium hover:underline"
                          title="Open client"
                        >
                          {clientName}
                        </Link>
                      ) : (
                        <span className="text-sm text-slate-500 dark:text-slate-400">—</span>
                      )}
                    </td>

                    {/* Status */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span
                        className={cx(
                          'px-2 py-0.5 rounded-full text-xs font-semibold',
                          WORK_STATUS_CHIP[item.status] ||
                            'bg-slate-100 text-slate-700 border border-slate-200'
                        )}
                      >
                        {prettyStatus(item.status)}
                      </span>
                    </td>

                    {/* Assignees */}
                    <td className="px-4 py-3">
                      <AssigneeGroup assignees={assignees} />
                    </td>

                    {/* Date */}
                    <td className="px-4 py-3">
                      <div className="text-sm text-slate-700 dark:text-slate-200">
                        {dateText}
                      </div>
                    </td>

                    {/* Actions */}
                    <td className="px-2 py-2 text-right">
                      <RowActions
                        item={item}
                        type={rowType}
                        canEdit={canEdit}
                        canTransition={canTransition}
                        canDelete={canDelete}
                        onEdit={onEdit}
                        onTransition={onTransition}
                        onDelete={onDelete}
                        onOpenDetails={onOpenDetails} // ✅ pass through
                      />
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Footer / Pagination */}
      {meta && (
        <div className="px-4 py-3 bg-slate-50 border-t flex flex-col md:flex-row md:items-center md:justify-between gap-3 dark:bg-slate-900 dark:border-slate-800">
          <div className="text-sm text-slate-600 dark:text-slate-300">
            Page <span className="font-semibold">{page}</span> of{' '}
            <span className="font-semibold">{totalPages}</span>
            {typeof total === 'number' && (
              <>
                {' '}
                • <span className="font-semibold">{total}</span> total
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              className="p-2 rounded-xl border bg-white disabled:opacity-40 dark:border-slate-800 dark:bg-slate-950"
              onClick={() => {
                if (onPageChange) onPageChange(Math.max(1, page - 1));
              }}
              disabled={page <= 1}
              title="Previous"
            >
              <FiChevronLeft />
            </button>
            <span className="text-sm text-slate-700 dark:text-slate-200">
              Page <span className="font-semibold">{page}</span> of{' '}
              <span className="font-semibold">{totalPages}</span>
            </span>
            <button
              className="p-2 rounded-xl border bg-white disabled:opacity-40 dark:border-slate-800 dark:bg-slate-950"
              onClick={() => {
                if (onPageChange) onPageChange(Math.min(totalPages, page + 1));
              }}
              disabled={page >= totalPages}
              title="Next"
            >
              <FiChevronRight />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
